<script setup lang="ts">
import { useI18n } from 'petite-vue-i18n';
import Heading from '../General/Heading.vue';
import { TEXT } from '../../configs/text';
import Button from '../General/Button.vue';
import { computed, ref } from 'vue';
import { ClassViewType, ClassStatus, classViewTypeMap } from '../../types/class';
import studentIcon from '../../assets/icons/student.svg';
import rankingIcon from '../../assets/icons/ranking.svg';
import groupIcon from '../../assets/icons/group.svg';
import StudentListView from './StudentListView.vue';
import StudentGroupView from './StudentGroupView.vue';
import Leaderboard from './Leaderboard.vue';
import { useClassStore } from '../../stores';
import { storeToRefs } from 'pinia';

const classStore = useClassStore()
const { selectedClassDetails: classDetails } = storeToRefs(classStore)
const currentViewType = ref<ClassViewType>(classDetails.value?.state === ClassStatus.on_going ? ClassViewType.group : ClassViewType.student)

const studentCount = computed<number>(() => {
    if (classDetails.value?.state === ClassStatus.on_going) {
        return classStore.attendedStudentCount
    }
    return classDetails.value?.students?.length || 0
})

function handleViewTypeSelect(viewType: ClassViewType) {
    if (currentViewType.value === viewType) return;
    currentViewType.value = viewType
}

const { t } = useI18n()
</script>

<template>
    <div class="bg-white rounded-lg p-4 md:p-8 flex flex-col flex-1 overflow-hidden">
        <div class="flex flex-wrap justify-between gap-3 mb-3">
            <Heading color="main" size="large" class="!mb-0">{{ t(classViewTypeMap[currentViewType]) }}</Heading>
            <div class="flex justify-end flex-wrap gap-2">
                <Button design="pill" size="small" color="warning" class="!text-black"
                    :class="{ 'opacity-50': currentViewType !== ClassViewType.leaderboard }"
                    :disabled="currentViewType === ClassViewType.leaderboard"
                    @click="handleViewTypeSelect(ClassViewType.leaderboard)"
                    v-if="classDetails?.state == ClassStatus.on_going">
                    <img :src="rankingIcon" />
                    {{ t(TEXT.leaderboard) }}
                </Button>
                <Button design="pill" size="small" color="warning" class="!text-black"
                    :class="{ 'opacity-50': currentViewType !== ClassViewType.student }"
                    :disabled="currentViewType === ClassViewType.student"
                    @click="handleViewTypeSelect(ClassViewType.student)">
                    <img :src="studentIcon" />
                    {{ t(TEXT.students) }}: {{ studentCount || 0 }}
                </Button>
                <Button design="pill" size="small" color="warning" class="!text-black"
                    :class="{ 'opacity-50': currentViewType !== ClassViewType.group }"
                    :disabled="currentViewType === ClassViewType.group"
                    @click="handleViewTypeSelect(ClassViewType.group)">
                    <img :src="groupIcon" />
                    {{ t(TEXT.group) }}: {{ classDetails?.groups?.length || 0 }}
                </Button>
            </div>
        </div>
        <StudentListView v-if="currentViewType === ClassViewType.student" />
        <StudentGroupView v-if="currentViewType === ClassViewType.group" />
        <Leaderboard
            v-if="currentViewType === ClassViewType.leaderboard && classDetails?.state == ClassStatus.on_going" />
    </div>
</template>